/**
 * WEB COPY of SubscriptionManagementScreen.tsx — picked by the web bundler
 * ahead of the mobile file (`.web.tsx` resolves first); Metro never sees it.
 *
 * The phone screen sells through the app stores. On the web, billing goes
 * through Stripe (plan Phase 7, decision D4): this screen shows the current
 * plan, opens Stripe's customer portal for cancel / change / card, lists
 * the tiers that have prices configured with a monthly / yearly switch,
 * offers the founding-member seat and the family plans (yearly, as in the
 * stores), and keeps the app's own promo-code field. Stripe returns here
 * after checkout with `?checkout=success` or `?checkout=cancelled`, and
 * after the customer portal with `?portal=return`.
 *
 * A plan bought on a phone is billed by Apple or Google; the web says so
 * and does not try to change it (the server refuses a second plan while a
 * store plan is paid for). App Store prices are never shown here.
 */
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  View,
  StyleSheet,
  SafeAreaView,
  TouchableOpacity,
  Image,
  ScrollView,
  TextInput,
  ActivityIndicator,
  Alert,
} from 'react-native';

import { AppText as Text } from '../../components/AppText';
import OrnamentBackground from '../../components/OrnamentBackground';
import { API_URL } from '../../config';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { getAuthToken } from '../../services/auth/tokenStorage';
import { securityService } from '../../services/securityService';
import {
  billingService,
  BillingPeriod,
  Catalog,
  CatalogTier,
  FamilyPlan,
  formatAmount,
  LadderTier,
  PaidTier,
} from '../../services/billingService';
import { createLogger } from '../../utils/logger';
import { ms, s } from '../../utils/responsive';

const logger = createLogger('SubscriptionManagement.web');

type BillingRouteProp = RouteProp<{ params?: { checkout?: string; portal?: string } }, 'params'>;

const TIER_LABEL: Record<string, string> = {
  trial: 'Free trial',
  expired: 'No plan',
  letter: 'Letter delivery',
  basic: 'Basic',
  plus: 'Plus',
  premium: 'Premium',
};

const LADDER: LadderTier[] = ['letter', 'basic', 'plus', 'premium'];

const TIER_INCLUDES: Record<LadderTier, string[]> = {
  letter: ['Name who receives your letter', 'Choose when it reaches them', 'Choose what releases it'],
  basic: ['Everything in Letter delivery', 'The full information vault, with Orion', 'Share access with successors and keyholders'],
  plus: ['Everything in Basic', 'Video messages (arriving soon)', '500MB for documents, on your phone and the web'],
  premium: ['Everything in Plus', '2GB for documents, on your phone and the web', 'Priority support'],
};

const FOUNDING_INCLUDES = [
  'Everything in Premium',
  '2GB for documents, on your phone and the web',
  'Priority support and early access to new features',
  'Your price stays the same for as long as you stay',
];

const FAMILY_INCLUDES: Record<FamilyPlan, string[]> = {
  basic_spouse: ['Two Basic accounts, for you and your spouse', 'Each with their own vault and letter', 'Less than two separate plans'],
  plus_spouse: ['Two Plus accounts', 'Video messages for both', '500MB document storage on each phone'],
  premium_spouse: ['Two Premium accounts', '2GB for documents each, on phone and web', 'Priority support'],
  family_bundle: ['Two Premium adults', 'Your children under 18, at no extra cost', '2GB for documents each, on phone and web'],
};

function friendlyDate(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d.toLocaleDateString();
}

export const SubscriptionManagementScreen: React.FC = () => {
  const navigation = useNavigation<any>();
  const route = useRoute<BillingRouteProp>();
  const checkoutResult = route.params?.checkout;
  const portalReturn = route.params?.portal === 'return';
  const {
    subscription,
    currentTier,
    trialDaysRemaining,
    isSuperUser,
    isFoundingMember,
    isFamilyMember,
    familyOwnerName,
    refreshSubscription,
  } = useSubscription();

  const [catalog, setCatalog] = useState<Catalog | null>(null);
  const [period, setPeriod] = useState<BillingPeriod>('yearly');
  const [busy, setBusy] = useState<string | null>(null);
  const [promoCode, setPromoCode] = useState('');
  const [applyingPromo, setApplyingPromo] = useState(false);
  const [banner, setBanner] = useState<{
    tone: 'good' | 'quiet';
    text: string;
    action?: { label: string; onPress: () => void };
  } | null>(null);
  const tierAtReturn = useRef(currentTier);
  // Set once the plan change has shown up, so the poll below stops and its
  // give-up message never replaces the success banner and its button.
  const planConfirmed = useRef(false);

  useEffect(() => {
    let cancelled = false;
    billingService.getCatalog().then((c) => {
      if (!cancelled) setCatalog(c);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  // Back from Stripe: the webhook usually lands within a few seconds; poll
  // the subscription until the tier changes or we give up. After the
  // portal there may be nothing to wait for (the person may have only
  // looked), so a few quiet refreshes are enough.
  useEffect(() => {
    if (checkoutResult === 'cancelled') {
      setBanner({ tone: 'quiet', text: 'No changes were made. Your letter and everything else stay as they were.' });
      return;
    }
    if (checkoutResult !== 'success' && !portalReturn) return;
    let attempts = 0;
    let cancelled = false;
    const limit = portalReturn ? 4 : 10;
    setBanner(
      portalReturn
        ? { tone: 'quiet', text: 'Back from Stripe. Any change you made will show here in a moment.' }
        : { tone: 'good', text: 'Thank you. Confirming your plan with Stripe…' },
    );
    const tick = async () => {
      attempts += 1;
      await refreshSubscription();
      if (cancelled || planConfirmed.current) return;
      if (attempts < limit) setTimeout(tick, 2000);
      else if (!portalReturn) {
        setBanner({ tone: 'quiet', text: 'Your payment went through. If the plan below has not updated yet, refresh in a moment.' });
      }
    };
    tick();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkoutResult, portalReturn]);

  // The phone offers the security options (authenticator, text codes, or
  // neither) right after an upgrade; Stripe's return lands here instead of
  // in that flow, so offer the same screen from the success banner.
  useEffect(() => {
    if (checkoutResult !== 'success' || currentTier === tierAtReturn.current) return;
    planConfirmed.current = true;
    const thanks = `You're on ${TIER_LABEL[currentTier] ?? currentTier}. Thank you.`;
    setBanner({ tone: 'good', text: thanks });
    // Only someone who has not chosen yet gets the offer: the options screen
    // skips itself for anyone with a recorded choice, so the button would
    // lead nowhere for an account that already has an authenticator.
    let cancelled = false;
    securityService
      .getPreference()
      .then((preference) => {
        if (cancelled || preference.decision !== null) return;
        setBanner({
          tone: 'good',
          text: `${thanks} Now is a good moment to add a second layer of security to your account.`,
          action: {
            label: 'Set up extra security',
            onPress: () => navigation.navigate('LetterFirst', { screen: 'SecurityOptions', params: { from: 'flow' } }),
          },
        });
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTier, checkoutResult]);

  const platform = subscription?.subscriptionPlatform ?? null;
  const onStripe = platform === 'stripe';
  const onStore = platform === 'apple' || platform === 'google';
  const status = subscription?.subscriptionStatus ?? 'expired';
  const endDate = friendlyDate(subscription?.subscriptionEndDate);
  const isPaid = (LADDER as string[]).includes(currentTier);
  const hasLivePlan = isPaid && (status === 'active' || status === 'grace_period' || status === 'cancelled');
  // Someone else's family plan covers this account: nothing to buy here.
  const coveredByFamily = isFamilyMember && !onStripe && !onStore;

  const statusLine = (): string => {
    if (isSuperUser) return 'Lifetime access.';
    if (isFamilyMember) return `Part of ${familyOwnerName ? `${familyOwnerName}'s` : 'a'} family plan.`;
    if (currentTier === 'trial') {
      return trialDaysRemaining !== null ? `Test drive · ${trialDaysRemaining} ${trialDaysRemaining === 1 ? 'day' : 'days'} left.` : 'Test drive.';
    }
    if (!isPaid) return 'No plan at the moment. Your letters stay yours either way.';
    if (status === 'grace_period') return `Payment failed · we'll retry. Access continues until ${endDate ?? 'the end of the period'}.`;
    // A plan set not to renew stays `active` until its end date; the API
    // says so with subscriptionAutoRenew === false.
    if (status === 'cancelled' || (status === 'active' && subscription?.subscriptionAutoRenew === false)) {
      return `Cancels on ${endDate ?? 'the end of the period'}.`;
    }
    if (status === 'expired') return `Ended${endDate ? ` on ${endDate}` : ''}.`;
    const interval = subscription?.billingInterval;
    return `Active${interval ? ` · billed ${interval}` : ''}${endDate ? ` · renews ${endDate}` : ''}.`;
  };

  const choose = async (tier: PaidTier, chosenPeriod: BillingPeriod = period) => {
    if (busy) return;
    try {
      setBusy(tier);
      await billingService.startCheckout(tier, chosenPeriod);
      // The browser is leaving for Stripe; nothing more to do here.
    } catch (error: any) {
      setBusy(null);
      const message = error?.response?.data?.message || error?.message || 'Could not open checkout. Please try again.';
      Alert.alert('Checkout', message);
    }
  };

  const manage = async () => {
    if (busy) return;
    try {
      setBusy('portal');
      await billingService.openPortal();
    } catch (error: any) {
      setBusy(null);
      Alert.alert('Billing', error?.response?.data?.message || 'Could not open the billing page. Please try again.');
    }
  };

  const applyPromo = useCallback(async () => {
    if (!promoCode.trim()) {
      Alert.alert('Promo code', 'Please enter a promo code.');
      return;
    }
    setApplyingPromo(true);
    try {
      const token = await getAuthToken();
      const response = await fetch(`${API_URL}/api/subscription/apply-promo`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: promoCode.trim().toUpperCase() }),
      });
      const data = await response.json();
      if (response.ok) {
        Alert.alert('Applied', data.message || 'Promo code applied.');
        setPromoCode('');
        await refreshSubscription();
      } else {
        Alert.alert('Promo code', data.message || 'That code is not valid.');
      }
    } catch (error) {
      logger.error('Promo apply failed:', error);
      Alert.alert('Promo code', 'Could not apply the code. Please try again.');
    } finally {
      setApplyingPromo(false);
    }
  }, [promoCode, refreshSubscription]);

  /** The line under a plan that cannot be chosen right now, or null when it can. */
  const blockedNote = (): string | null => {
    if (coveredByFamily) return 'Your access comes with a family plan.';
    if (onStripe && hasLivePlan) return 'Change plans from Manage billing.';
    if (onStore && hasLivePlan) return "Your plan is billed through your phone's app store. Change it there.";
    return null;
  };

  const chooseButton = (tier: PaidTier, label: string, available: boolean, chosenPeriod?: BillingPeriod, tint?: string) => (
    <TouchableOpacity
      style={[styles.chooseButton, tint ? { backgroundColor: tint } : null, (!available || !!busy) && styles.disabled]}
      onPress={() => choose(tier, chosenPeriod)}
      disabled={!available || !!busy}
      accessibilityRole="button"
      accessibilityLabel={label}
    >
      {busy === tier ? <ActivityIndicator color="#FFFFFF" /> : <Text style={styles.chooseButtonText}>{label}</Text>}
    </TouchableOpacity>
  );

  const includes = (lines: string[], tint?: string) =>
    lines.map((line) => (
      <View key={line} style={styles.includeRow}>
        <Image source={require('../../assets/icons/check.png')} style={[styles.includeIcon, tint ? { tintColor: tint } : null]} />
        <Text style={styles.includeText}>{line}</Text>
      </View>
    ));

  const renderTier = (entry: CatalogTier) => {
    const price = period === 'monthly' ? entry.monthly : entry.yearly;
    const other = period === 'monthly' ? entry.yearly : entry.monthly;
    const isCurrent = !isFoundingMember && currentTier === entry.tier;
    const available = price !== null;
    const note = blockedNote();
    return (
      <View key={entry.tier} style={[styles.planCard, isCurrent && styles.planCardCurrent]}>
        <View style={styles.planHead}>
          <View style={styles.planTitles}>
            <Text style={styles.planName}>{entry.name}</Text>
            <Text style={styles.planHeadline}>{entry.headline}</Text>
          </View>
          <View style={styles.planPriceBlock}>
            {available ? (
              <>
                <Text style={styles.planPrice}>{formatAmount(price)}</Text>
                <Text style={styles.planPer}>/{period === 'monthly' ? 'month' : 'year'}</Text>
              </>
            ) : (
              <Text style={styles.planPer}>{other ? `${period === 'monthly' ? 'Yearly' : 'Monthly'} only` : 'Not on the web yet'}</Text>
            )}
          </View>
        </View>
        {includes(TIER_INCLUDES[entry.tier as LadderTier] ?? [])}
        {isCurrent ? (
          <View style={styles.currentPill}>
            <Text style={styles.currentPillText}>Current plan</Text>
          </View>
        ) : note ? (
          <Text style={styles.planNote}>{note}</Text>
        ) : (
          chooseButton(entry.tier, `Choose ${entry.name}`, available)
        )}
      </View>
    );
  };

  const renderFounding = () => {
    const entry = catalog?.founding;
    if (!entry || isSuperUser) return null;
    const price = entry.yearly;
    if (isFoundingMember) {
      return (
        <View style={[styles.planCard, styles.foundingCard, styles.foundingCardCurrent]}>
          <View style={styles.foundingBadge}>
            <Text style={styles.foundingBadgeText}>FOUNDING MEMBER</Text>
          </View>
          <View style={styles.planHead}>
            <View style={styles.planTitles}>
              <Text style={styles.planName}>{entry.name}</Text>
              <Text style={styles.planHeadline}>One of our first {entry.totalSlots}. Thank you for your early support.</Text>
            </View>
            <View style={styles.planPriceBlock}>
              <Text style={[styles.planPrice, styles.foundingPrice]}>{formatAmount(price)}</Text>
              <Text style={styles.planPer}>/year</Text>
            </View>
          </View>
          {includes(FOUNDING_INCLUDES, '#7C3AED')}
          <View style={[styles.currentPill, styles.foundingPill]}>
            <Text style={[styles.currentPillText, styles.foundingPillText]}>Current plan</Text>
          </View>
        </View>
      );
    }
    if (!entry.available) return null;
    const note = blockedNote();
    return (
      <View style={[styles.planCard, styles.foundingCard]}>
        <View style={styles.foundingBadge}>
          <Text style={styles.foundingBadgeText}>
            LIMITED · {entry.slotsRemaining} of {entry.totalSlots} left
          </Text>
        </View>
        <View style={styles.planHead}>
          <View style={styles.planTitles}>
            <Text style={styles.planName}>{entry.name}</Text>
            <Text style={styles.planHeadline}>{entry.headline}</Text>
          </View>
          <View style={styles.planPriceBlock}>
            <Text style={[styles.planPrice, styles.foundingPrice]}>{formatAmount(price)}</Text>
            <Text style={styles.planPer}>/year</Text>
          </View>
        </View>
        {includes(FOUNDING_INCLUDES, '#7C3AED')}
        {note ? (
          <Text style={styles.planNote}>{note}</Text>
        ) : (
          chooseButton('founding', 'Become a Founding Member', price !== null, 'yearly', '#7C3AED')
        )}
      </View>
    );
  };

  const renderFamily = (entry: CatalogTier) => {
    const price = entry.yearly;
    const featured = entry.tier === 'family_bundle';
    const note = blockedNote();
    return (
      <View key={entry.tier} style={[styles.planCard, featured && styles.planCardFeatured]}>
        {featured && (
          <View style={styles.featuredBadge}>
            <Text style={styles.featuredBadgeText}>BEST VALUE</Text>
          </View>
        )}
        <View style={styles.planHead}>
          <View style={styles.planTitles}>
            <Text style={styles.planName}>{entry.name}</Text>
            <Text style={styles.planHeadline}>{entry.headline}</Text>
          </View>
          <View style={styles.planPriceBlock}>
            <Text style={styles.planPrice}>{formatAmount(price)}</Text>
            <Text style={styles.planPer}>/year</Text>
          </View>
        </View>
        {includes(FAMILY_INCLUDES[entry.tier as FamilyPlan] ?? [])}
        {note ? <Text style={styles.planNote}>{note}</Text> : chooseButton(entry.tier, `Choose ${entry.name}`, price !== null, 'yearly')}
      </View>
    );
  };

  const tiers = catalog?.tiers ?? [];
  const family = catalog?.family ?? [];

  return (
    <SafeAreaView style={styles.container}>
      <OrnamentBackground />
      <View style={styles.header}>
        <TouchableOpacity
          style={styles.backButton}
          onPress={() => (navigation.canGoBack() ? navigation.goBack() : navigation.navigate('Settings'))}
          accessibilityRole="button"
          accessibilityLabel="Go back"
        >
          <Image source={require('../../assets/icons/back.png')} style={styles.backIcon} />
        </TouchableOpacity>
        <Text style={styles.headerTitle}>Billing</Text>
        <View style={styles.headerSpacer} />
      </View>

      <View style={styles.sheet}>
        <ScrollView showsVerticalScrollIndicator={false}>
          {banner && (
            <View style={[styles.banner, banner.tone === 'good' ? styles.bannerGood : styles.bannerQuiet]}>
              <Text style={styles.bannerText}>{banner.text}</Text>
              {banner.action && (
                <TouchableOpacity
                  style={styles.bannerButton}
                  onPress={banner.action.onPress}
                  accessibilityRole="button"
                  accessibilityLabel={banner.action.label}
                >
                  <Text style={styles.bannerButtonText}>{banner.action.label}</Text>
                </TouchableOpacity>
              )}
            </View>
          )}

          <View style={styles.currentCard}>
            <Text style={styles.sectionLabel}>Your plan</Text>
            <Text style={styles.currentTier}>
              {isSuperUser ? 'Premium' : TIER_LABEL[currentTier] ?? currentTier}
              {isFoundingMember ? ' · Founding member' : ''}
            </Text>
            <Text style={styles.currentStatus}>{statusLine()}</Text>
            {onStripe && (
              <TouchableOpacity
                style={[styles.manageButton, busy === 'portal' && styles.disabled]}
                onPress={manage}
                disabled={!!busy}
                accessibilityRole="button"
                accessibilityLabel="Manage billing"
              >
                {busy === 'portal' ? (
                  <ActivityIndicator color="#1D5C59" />
                ) : (
                  <Text style={styles.manageButtonText}>Manage billing</Text>
                )}
              </TouchableOpacity>
            )}
            {onStripe && (
              <Text style={styles.manageHint}>Cancel, change plan, or update your card. Stripe handles the page.</Text>
            )}
            {onStore && (
              <Text style={styles.manageHint}>
                This plan is billed through your phone's app store. Manage it in the FinalKey app on your phone.
              </Text>
            )}
          </View>

          {!isSuperUser && !coveredByFamily && (
            <>
              <View style={styles.plansHead}>
                <Text style={styles.sectionLabel}>Plans</Text>
                <View style={styles.periodToggle}>
                  {(['monthly', 'yearly'] as BillingPeriod[]).map((p) => (
                    <TouchableOpacity
                      key={p}
                      style={[styles.periodOption, period === p && styles.periodOptionOn]}
                      onPress={() => setPeriod(p)}
                      accessibilityRole="button"
                      accessibilityLabel={p === 'monthly' ? 'Monthly prices' : 'Yearly prices'}
                      accessibilityState={{ selected: period === p }}
                    >
                      <Text style={[styles.periodText, period === p && styles.periodTextOn]}>
                        {p === 'monthly' ? 'Monthly' : 'Yearly'}
                      </Text>
                    </TouchableOpacity>
                  ))}
                </View>
              </View>

              {catalog === null ? (
                <ActivityIndicator color="#29BFC0" style={styles.loading} />
              ) : !catalog.enabled || tiers.length === 0 ? (
                <View style={styles.emptyCard}>
                  <Text style={styles.emptyText}>
                    Web checkout is not set up yet. Until it is, plans can be chosen in the FinalKey app on your phone.
                  </Text>
                </View>
              ) : (
                <>
                  {renderFounding()}
                  {tiers.map(renderTier)}
                </>
              )}
              <Text style={styles.footnote}>Prices in US dollars. Cancel any time; your letters stay yours either way.</Text>

              {family.length > 0 && (
                <>
                  <View style={styles.plansHead}>
                    <Text style={styles.sectionLabel}>Family plans</Text>
                    <Text style={styles.plansAside}>Billed yearly</Text>
                  </View>
                  {family.map(renderFamily)}
                  <TouchableOpacity
                    style={styles.familyLink}
                    onPress={() => navigation.navigate('FamilyPlan')}
                    accessibilityRole="button"
                    accessibilityLabel="Manage family members"
                  >
                    <Text style={styles.familyLinkText}>Already have a family plan? Manage members →</Text>
                  </TouchableOpacity>
                </>
              )}
            </>
          )}

          <View style={styles.promoCard}>
            <Text style={styles.sectionLabel}>Have a promo code?</Text>
            <View style={styles.promoRow}>
              <TextInput
                style={styles.promoInput}
                value={promoCode}
                onChangeText={setPromoCode}
                placeholder="Enter code"
                placeholderTextColor="#A0AEC0"
                autoCapitalize="characters"
                autoCorrect={false}
                accessibilityLabel="Promo code"
              />
              <TouchableOpacity
                style={[styles.promoButton, applyingPromo && styles.disabled]}
                onPress={applyPromo}
                disabled={applyingPromo}
                accessibilityRole="button"
                accessibilityLabel="Apply promo code"
              >
                {applyingPromo ? <ActivityIndicator color="#FFFFFF" /> : <Text style={styles.promoButtonText}>Apply</Text>}
              </TouchableOpacity>
            </View>
            <Text style={styles.promoHint}>Stripe discount codes are entered on the checkout page itself.</Text>
          </View>

          <View style={styles.bottomPadding} />
        </ScrollView>
      </View>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#29BFC0' },
  header: { height: s(56), flexDirection: 'row', alignItems: 'center', paddingHorizontal: ms(16) },
  backButton: { padding: ms(8) },
  backIcon: { width: s(28), height: s(28), tintColor: '#fff' },
  headerTitle: { flex: 1, textAlign: 'center', fontSize: ms(17, 0.3), fontWeight: '600', color: '#FFFFFF' },
  headerSpacer: { width: s(44) },
  sheet: {
    flex: 1,
    backgroundColor: '#F3F6F8',
    borderTopLeftRadius: ms(20),
    borderTopRightRadius: ms(20),
    paddingTop: ms(20),
    paddingHorizontal: ms(16),
  },
  banner: { borderRadius: ms(12), padding: ms(14), marginBottom: ms(14) },
  bannerGood: { backgroundColor: '#DFF2F1' },
  bannerQuiet: { backgroundColor: '#FFF0E0' },
  bannerText: { fontSize: ms(14, 0.3), lineHeight: ms(20, 0.3), color: '#1C2C4F' },
  bannerButton: {
    alignSelf: 'flex-start',
    backgroundColor: '#1D5C59',
    borderRadius: ms(20),
    paddingVertical: ms(8),
    paddingHorizontal: ms(14),
    marginTop: ms(10),
  },
  bannerButtonText: { color: '#FFFFFF', fontSize: ms(13.5, 0.3), fontWeight: '700' },
  sectionLabel: {
    fontSize: ms(12, 0.3),
    fontWeight: '700',
    letterSpacing: 1,
    textTransform: 'uppercase',
    color: '#1D5C59',
    marginBottom: ms(8),
  },
  currentCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: ms(14),
    borderWidth: ms(1),
    borderColor: '#E5E5E5',
    padding: ms(16),
    marginBottom: ms(18),
  },
  currentTier: { fontSize: ms(22, 0.3), fontWeight: 'bold', color: '#1C2C4F' },
  currentStatus: { fontSize: ms(14, 0.3), lineHeight: ms(20, 0.3), color: '#596B85', marginTop: ms(4) },
  manageButton: {
    alignSelf: 'flex-start',
    backgroundColor: '#DFF2F1',
    borderRadius: ms(25),
    paddingVertical: ms(10),
    paddingHorizontal: ms(18),
    marginTop: ms(12),
  },
  manageButtonText: { color: '#1D5C59', fontSize: ms(14, 0.3), fontWeight: '700' },
  manageHint: { fontSize: ms(12.5, 0.3), lineHeight: ms(18, 0.3), color: '#596B85', marginTop: ms(8) },
  plansHead: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: ms(4) },
  plansAside: { fontSize: ms(12.5, 0.3), color: '#596B85', marginBottom: ms(8) },
  periodToggle: { flexDirection: 'row', backgroundColor: '#E9EEF1', borderRadius: ms(20), padding: 3, marginBottom: ms(8) },
  periodOption: { paddingVertical: ms(6), paddingHorizontal: ms(14), borderRadius: ms(17) },
  periodOptionOn: { backgroundColor: '#FFFFFF' },
  periodText: { fontSize: ms(13, 0.3), fontWeight: '600', color: '#596B85' },
  periodTextOn: { color: '#1C2C4F' },
  loading: { marginVertical: ms(24) },
  planCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: ms(14),
    borderWidth: ms(1),
    borderColor: '#E5E5E5',
    padding: ms(16),
    marginBottom: ms(12),
  },
  planCardCurrent: { borderColor: '#29BFC0', borderWidth: 2 },
  planCardFeatured: { borderColor: '#FF9F5A', borderWidth: 2 },
  foundingCard: { borderColor: '#7C3AED', borderWidth: 2, backgroundColor: '#FBF8FF' },
  foundingCardCurrent: { backgroundColor: '#FFFFFF' },
  foundingBadge: {
    alignSelf: 'flex-start',
    backgroundColor: '#7C3AED',
    borderRadius: ms(10),
    paddingVertical: ms(4),
    paddingHorizontal: ms(10),
    marginBottom: ms(10),
  },
  foundingBadgeText: { color: '#FFFFFF', fontSize: ms(11, 0.3), fontWeight: '700', letterSpacing: 0.8 },
  foundingPrice: { color: '#7C3AED' },
  foundingPill: { backgroundColor: '#F1E9FF' },
  foundingPillText: { color: '#5B21B6' },
  featuredBadge: {
    alignSelf: 'flex-start',
    backgroundColor: '#FF9F5A',
    borderRadius: ms(10),
    paddingVertical: ms(4),
    paddingHorizontal: ms(10),
    marginBottom: ms(10),
  },
  featuredBadgeText: { color: '#FFFFFF', fontSize: ms(11, 0.3), fontWeight: '700', letterSpacing: 0.8 },
  planHead: { flexDirection: 'row', alignItems: 'flex-start', justifyContent: 'space-between', gap: ms(12), marginBottom: ms(10) },
  planTitles: { flex: 1 },
  planName: { fontSize: ms(17, 0.3), fontWeight: '700', color: '#1C2C4F' },
  planHeadline: { fontSize: ms(13, 0.3), lineHeight: ms(18, 0.3), color: '#596B85', marginTop: ms(2) },
  planPriceBlock: { alignItems: 'flex-end' },
  planPrice: { fontSize: ms(22, 0.3), fontWeight: 'bold', color: '#29BFC0' },
  planPer: { fontSize: ms(12, 0.3), color: '#596B85' },
  includeRow: { flexDirection: 'row', alignItems: 'flex-start', gap: ms(8), marginBottom: ms(6) },
  includeIcon: { width: s(16), height: s(16), tintColor: '#29BFC0', marginTop: ms(2) },
  includeText: { flex: 1, fontSize: ms(13.5, 0.3), lineHeight: ms(19, 0.3), color: '#1C2C4F' },
  chooseButton: {
    backgroundColor: '#29BFC0',
    borderRadius: ms(25),
    paddingVertical: ms(12),
    alignItems: 'center',
    marginTop: ms(10),
  },
  chooseButtonText: { color: '#FFFFFF', fontSize: ms(15, 0.3), fontWeight: '700' },
  currentPill: {
    alignSelf: 'flex-start',
    backgroundColor: '#DFF2F1',
    borderRadius: ms(20),
    paddingVertical: ms(6),
    paddingHorizontal: ms(12),
    marginTop: ms(8),
  },
  currentPillText: { color: '#1D5C59', fontSize: ms(12.5, 0.3), fontWeight: '700' },
  planNote: { fontSize: ms(12.5, 0.3), color: '#596B85', marginTop: ms(8) },
  emptyCard: { backgroundColor: '#FFFFFF', borderRadius: ms(14), borderWidth: ms(1), borderColor: '#E5E5E5', padding: ms(16), marginBottom: ms(12) },
  emptyText: { fontSize: ms(14, 0.3), lineHeight: ms(20, 0.3), color: '#1C2C4F' },
  footnote: { fontSize: ms(12, 0.3), lineHeight: ms(17, 0.3), color: '#6B7280', textAlign: 'center', marginTop: ms(4), marginBottom: ms(18) },
  familyLink: { alignSelf: 'center', paddingVertical: ms(8), marginBottom: ms(18) },
  familyLinkText: { fontSize: ms(13.5, 0.3), fontWeight: '600', color: '#1D5C59' },
  promoCard: { backgroundColor: '#FFFFFF', borderRadius: ms(14), borderWidth: ms(1), borderColor: '#E5E5E5', padding: ms(16) },
  promoRow: { flexDirection: 'row', gap: ms(10), alignItems: 'center' },
  promoInput: {
    flex: 1,
    borderWidth: 1,
    borderColor: '#DCE3E8',
    borderRadius: ms(10),
    paddingVertical: ms(10),
    paddingHorizontal: ms(12),
    fontSize: ms(15, 0.3),
    color: '#1C2C4F',
    backgroundColor: '#FFFFFF',
  },
  promoButton: { backgroundColor: '#29BFC0', borderRadius: ms(25), paddingVertical: ms(11), paddingHorizontal: ms(18) },
  promoButtonText: { color: '#FFFFFF', fontSize: ms(14, 0.3), fontWeight: '700' },
  promoHint: { fontSize: ms(12, 0.3), color: '#6B7280', marginTop: ms(8) },
  disabled: { opacity: 0.5 },
  bottomPadding: { height: s(40) },
});

export default SubscriptionManagementScreen;
